// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { xHasRoles } from "@sc/App/Store/Selectors/SessionSelectors";
import { USER_ROLES } from "@sc/XDL/constants";
import { parseBoolean } from "@sc/XDL/Utilities";

export const xIsAuthenticated = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
};

export const xHasRole = (state: ApplicationState, role: USER_ROLES, exactMatch = false) => {
    const roles = state.context.session.userRoles;
    if (exactMatch) {
        return roles === role; // User don't have other roles
    }
    return roles?.indexOf(role) > -1;
};

export const getLogoImage = (state: ApplicationState) => {
    const logoImage = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields.logoImage;
    return logoImage;
};

export const getMainNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/MainNavigation";
    })?.fields;
};
export const getHeaderSearchWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderSearchInput";
    })?.fields;
};

export const getCartLinkWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/CartLink";
    })?.fields;
};

export const getHeaderSignInWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/HeaderSignIn";
    })?.fields;
};

export const getLogoImageWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields;
};

export const xGetShipToAddressLabel = (state: ApplicationState) => {
    const { isAuthenticated, rememberMe, isGuest, shipToId } = getSession(state);
    const shipToState = getShipToState(state, shipToId);
    const { value: shipTo } = shipToState;
    let addressLabel = "";
    if (xIsAuthenticated(state) && shipTo) {
        addressLabel = `${shipTo.companyName}, ${shipTo.fullAddress}`;
    }
    return addressLabel;
};

export const xGetFulfillmentLabel = (state: ApplicationState) => {
    const { fulfillmentMethod, isAuthenticated, rememberMe, isGuest, shipToId, pickUpWarehouse } = getSession(state);
    const shipToState = getShipToState(state, shipToId);
    const { value: shipTo } = shipToState;
    let addressLabel = "";
    if (fulfillmentMethod === FulfillmentMethod.Ship && xIsAuthenticated(state) && shipTo) {
        addressLabel = `${shipTo.companyName}, ${shipTo.fullAddress}`;
    } else if (fulfillmentMethod === FulfillmentMethod.PickUp) {
        addressLabel = pickUpWarehouse ? `${pickUpWarehouse.description || pickUpWarehouse.name}` : "";
    }

    return addressLabel;
};

export const getHeaderShipToAddress = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/HeaderShipToAddress";
    });
};

export const getContactNumberWidgetFields = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/ContactNumber";
    })?.fields;
};

export const canSeeInvoiceHistoryLink = (state: ApplicationState): boolean | undefined => {
    return xHasRoles(state, [USER_ROLES.ADMIN, USER_ROLES.BUYER3], "one-or-more");
};
export const getEmptyCartAlertWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "EmptyCartAlert/EmptyCartAlert";
    })?.fields;
};
export const hidePrice = (state: ApplicationState) => {
    return parseBoolean(getSession(state)?.properties?.hidePrices);
};
